<template>
  <div class="tabs-container big-m-t big-m-b d-f jc-c p">
    <div class="w-100">
      <div class="d-f fd-c ai-c m-b-children">
        <div class="d-f w-100">
          <div
            v-for="(tab, i) in tabs"
            :key="i"
            :class="selectedTabIndex === i ? 'selected' : ''"
            class="flex-1 ta-c m-b p-t p-b b-bottom tab tt-up"
            @click="onTabClick(i)"
          >
            {{ tab }}
          </div>
        </div>

        <SettingsLabelProfile
          v-if="selectedTabIndex === 0 && labelSettings"
          ref="profile"
          :label-settings="labelSettings"
          @nextTab="selectedTabIndex++"
          @saveSettings="saveLabelSettings"
        />

        <SettingsLabelDropbox
          v-if="selectedTabIndex === 1 && labelSettings"
          ref="dropbox"
          :label-settings="labelSettings"
          @nextTab="selectedTabIndex++"
          @saveSettings="saveLabelSettings"
        />

        <SettingsLabelReviewers
          v-if="selectedTabIndex === 2 && labelSettings"
          ref="reviewers"
          :label-settings="labelSettings"
          @saveSettings="saveLabelSettings"
        />

        <EventsEditor
          v-if="selectedTabIndex === 3"
          ref="events"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import SignUp from "@/components/settings/SignUp.vue";
import SettingsLabelProfile from "@/components/settings/SettingsLabelProfile.vue";
import { auth, rtdb, storage } from "@/db";
import SettingsLabelDropbox from "@/components/settings/SettingsLabelDropbox.vue";
import SettingsLabelReviewers from "@/components/settings/SettingsLabelReviewers.vue";
import EventsEditor from "@/components/settings/EventsEditor.vue";
import { parseInt } from "lodash";

export default {
  name: "SettingsLabel",
  components: {
    SettingsLabelReviewers,
    SettingsLabelDropbox,
    SettingsLabelProfile,
    EventsEditor,
    SignUp,
  },
  mounted() {
    if ('tabIndex' in this.$route.query) {
      this.selectedTabIndex = parseInt(this.$route.query.tabIndex);
    }
  },
  data() {
    return {
      selectedTabIndex: 0,
    };
  },
  methods: {
    // when clicking a tab, check current tab component for unsaved changes and confirm
    onTabClick(i) {
      if (i === this.selectedTabIndex) return;

      // map tab index -> child ref name
      const refMap = ["profile", "dropbox", "reviewers", "events"];
      const currentRef = this.$refs[refMap[this.selectedTabIndex]];

      if (currentRef) {
        // prefer a boolean property `dataChanged`, else call `hasUnsavedChanges()` if available
        let hasUnsaved = false;
        if (typeof currentRef.dataChanged !== "undefined") {
          hasUnsaved = !!currentRef.dataChanged;
        } else if (typeof currentRef.hasUnsavedChanges === "function") {
          try {
            hasUnsaved = !!currentRef.hasUnsavedChanges();
          } catch (e) {
            console.error("hasUnsavedChanges error", e);
          }
        }

        if (hasUnsaved) {
          const ok = window.confirm(
            "You have unsaved changes, do you want to continue?"
          );
          if (!ok) return; // abort switching
        }
      }

      // safe to switch
      this.selectedTabIndex = i;
    },
    async saveLabelSettings(settings, onComplete) {
      if (Object.keys(settings).length === 0) {
        onComplete?.(true);
        return;
      }

      // Check username uniqueness if username is being changed
      // Note: settings.username is already lowercase from generation, but ensure it is
      if (settings.username && settings.username.toLowerCase() !== (this.labelSettings.username || "").toLowerCase()) {
        const usernameToCheck = settings.username.toLowerCase();
        const encodedUsername = encodeURIComponent(usernameToCheck).replace(/\./g, "%2E");
        const currentUid = auth.currentUser.uid;
        
        try {
          // Check both labelSettings and usernames node
          const [labelCheck, usernameCheck] = await Promise.all([
            rtdb
              .ref("labelSettings")
              .orderByChild("username")
              .equalTo(usernameToCheck)
              .once("value"),
            rtdb
              .ref("usernames")
              .child(encodedUsername)
              .once("value")
          ]);
          
          // Check if username exists in labelSettings
          if (labelCheck.exists()) {
            const existingLabels = labelCheck.val();
            const existingLabelEntry = Object.entries(existingLabels)[0];
            const existingUid = existingLabelEntry[0]; // uid is the key
            const existingLabel = existingLabelEntry[1]; // label data
            
            // If username exists and belongs to a different user, prevent update
            // Check both the key (uid) and the data.uid field if it exists
            if (existingUid !== currentUid || (existingLabel.uid && existingLabel.uid !== currentUid)) {
              const errorMessage = "Oops! That username is taken";
              if (onComplete) {
                onComplete(false, errorMessage);
              }
              return;
            }
            // If it's the same user, allow the update (no change needed)
          }
          
          // Check if username exists in usernames node (could be from artists or other labels)
          if (usernameCheck.exists()) {
            const existingUidInUsernames = usernameCheck.val();
            // If username exists and belongs to a different user, prevent update
            if (existingUidInUsernames !== currentUid) {
              const errorMessage = "Oops! That username is taken";
              if (onComplete) {
                onComplete(false, errorMessage);
              }
              return;
            }
            // If it's the same user, allow the update (no change needed)
          }
        } catch (error) {
          console.error("Error checking username:", error);
          const errorMessage = "Error checking username availability. Please try again.";
          if (onComplete) {
            onComplete(false, errorMessage);
          }
          return;
        }
      }

      return Promise.resolve()
        .then(() => {
          if ("logo" in settings) {
            const storageRef = storage.ref(
              `labelSettings/${auth.currentUser.uid}/profileImage`
            );
            if (settings.logo) {
              return storageRef
                .putString(settings.logo, "data_url")
                .then((snapshot) => snapshot.ref.getDownloadURL())
                .then((url) => {
                  settings.logo = url;
                });
            } else {
              // Remove logo
              settings.logo = null;
              return storageRef.delete();
            }
          }
        })
        .then(() => {
          if (settings.reviewers) {
            const promises = Object.entries(settings.reviewers).map(
              ([key, value]) => {
                if (
                  value.profileImage &&
                  value.profileImage.startsWith("data:")
                ) {
                  return storage
                    .ref(
                      `labelSettings/${auth.currentUser.uid}/reviewers/${key}`
                    )
                    .putString(value.profileImage, "data_url")
                    .then((snapshot) => snapshot.ref.getDownloadURL())
                    .then((url) => {
                      settings.reviewers[key].profileImage = url;
                    });
                } else {
                  return Promise.resolve();
                }
              }
            );
            return Promise.all(promises);
          }
        })
        .then(() => {
          // If username is being changed, update the usernames node
          if (settings.username && settings.username.toLowerCase() !== (this.labelSettings.username || "").toLowerCase()) {
            const usernamesRef = rtdb.ref("usernames");
            const oldUsername = this.labelSettings.username;
            const newUsername = settings.username.toLowerCase();
            
            // Encode the username the same way artists do (for consistency and special character handling)
            const encodedNewUsername = encodeURIComponent(newUsername).replace(/\./g, "%2E");
            
            // Add new username to usernames node
            return usernamesRef
              .update({
                [encodedNewUsername]: auth.currentUser.uid
              })
              .then(() => {
                // Remove old username if it exists
                if (oldUsername) {
                  const encodedOldUsername = encodeURIComponent(oldUsername).replace(/\./g, "%2E");
                  return usernamesRef.child(encodedOldUsername).remove();
                }
              });
          }
        })
        .then(() =>
          rtdb.ref(`labelSettings/${auth.currentUser.uid}`).update(settings)
        )
        .then(() => {
          // Update store so own profile view reflects changes when they navigate back
          this.$store.commit("SET_LABEL_SETTINGS", {
            ...this.labelSettings,
            ...settings,
          });
          // If username changed, navigate to new profile URL
          if (settings.username && settings.username !== this.labelSettings.username) {
            this.$router.push({
              name: "Profile",
              params: { id: settings.username }
            });
          }
          
          onComplete?.(true);
        })
        .catch((e) => {
          console.error(e);
          const errorMessage = "Failed to save settings";
          if (onComplete) {
            onComplete(false, errorMessage);
          }
        });
    },
  },
  computed: {
    ...mapState(["labelSettings"]),
    tabs() {
      return [
        "Label Profile",
        `Demo Drop (${
          this.labelSettings?.acceptingSubmissions ? "ON" : "OFF"
        })`,
        `Reviewers (${Object.keys(this.labelSettings?.reviewers ?? {}).length})`,
        "Events",
      ];
    },
  },
  watch: {
    labelSettings() {},
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";
</style>
